import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Cog, Printer, FileText, Send, Save, Factory, Ribbon, MapPin, ShieldCheck, Wind } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import Anagrafiche from "@/pages/Anagrafiche";
import Ubicazioni from "@/pages/Ubicazioni";
import Permessi from "@/pages/Permessi";
import EstrusioneMacchineTab from "@/pages/EstrusioneMacchineTab";

function StampanteBobineCard() {
    const [cfg, setCfg] = useState({ host: "", port: 9100 });
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [ready, setReady] = useState(false);
    useEffect(() => {
        api.get("/impostazioni/stampante-bobine").then(r => setCfg(r.data)).catch(e => toast.error(formatApiError(e))).finally(() => setReady(true));
    }, []);
    const save = async () => {
        setBusy(true);
        try {
            await api.put("/impostazioni/stampante-bobine", { host: cfg.host || null, port: parseInt(cfg.port) || 9100 });
            setDirty(false); toast.success("Stampante bobine salvata");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const test = async () => {
        setBusy(true);
        try { const { data } = await api.post("/impostazioni/stampante-bobine/test"); toast.success(data.message); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <Card className="card p-5 md:p-6 h-fit" data-testid="card-stampante-bobine">
            <div className="flex items-center gap-3 mb-5">
                <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-300"><Wind size={18} /></div>
                <div>
                    <h2 className="font-display font-extrabold text-lg">Zebra — Etichette bobine</h2>
                    <p className="text-xs text-slate-500">Reparto estrusione · invio diretto TCP (ZPL)</p>
                </div>
            </div>
            <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                        <Label className="text-[10px] text-slate-500 uppercase tracking-widest">IP / Host</Label>
                        <Input data-testid="bob-printer-host" disabled={!ready} value={cfg.host || ""} placeholder="192.168.1.60"
                               onChange={e=>{setCfg({...cfg, host: e.target.value}); setDirty(true);}}
                               className="bg-slate-950/50 border-slate-800 mt-1 font-mono h-9" />
                    </div>
                    <div>
                        <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Porta</Label>
                        <Input data-testid="bob-printer-port" disabled={!ready} type="number" value={cfg.port || 9100}
                               onChange={e=>{setCfg({...cfg, port: e.target.value}); setDirty(true);}}
                               className="bg-slate-950/50 border-slate-800 mt-1 font-mono h-9" />
                    </div>
                </div>
                <div className="text-[11px] text-slate-500 leading-relaxed border-l-2 border-cyan-500/50 pl-3">
                    L'etichetta (n° bobina + barcode, articolo, peso, macchina, operatore, data, QR) viene stampata alla registrazione della bobina e ristampabile dalla lista. Se l'IP è vuoto viene scaricato il file ZPL.
                </div>
                <div className="flex gap-2">
                    <Button size="sm" onClick={save} disabled={!dirty || busy} data-testid="bob-printer-save"
                            className={`gap-1 ${dirty ? "bg-amber-500 hover:bg-amber-400 text-slate-950" : "bg-slate-800 text-slate-500"}`}>
                        <Save size={12} /> Salva
                    </Button>
                    <Button size="sm" variant="outline" onClick={test} disabled={!cfg.host || dirty || busy} data-testid="bob-printer-test"
                            className="bg-transparent border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 gap-1">
                        <Send size={12} /> {busy ? "Invio…" : "Prova stampa"}
                    </Button>
                </div>
            </div>
        </Card>
    );
}

function StampantiTab() {
    const [impianti, setImpianti] = useState([]);
    const [saving, setSaving] = useState({});
    const [testing, setTesting] = useState({});
    const [a4, setA4] = useState({ host: "", port: 9100 });
    const [a4Dirty, setA4Dirty] = useState(false);
    const [a4Busy, setA4Busy] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: imps }, { data: cfg }] = await Promise.all([
                api.get("/impianti/"), api.get("/impostazioni/stampante-a4")
            ]);
            setImpianti(imps); setA4(cfg); setA4Dirty(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const updateImp = (id, patch) => setImpianti(list => list.map(i => i.id === id ? { ...i, ...patch, _dirty: true } : i));

    const saveImp = async (imp) => {
        setSaving(s => ({ ...s, [imp.id]: true }));
        try {
            const payload = {
                codice: imp.codice, descrizione: imp.descrizione,
                larghezza_rullo_cm: imp.larghezza_rullo_cm || null,
                piste_max: imp.piste_max || null,
                printer_host: (imp.printer_host || "").trim() || null,
                printer_port: parseInt(imp.printer_port) || 9100,
                note: imp.note || null,
            };
            await api.put(`/impianti/${imp.id}`, payload);
            toast.success(`${imp.codice}: salvato`); load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(s => ({ ...s, [imp.id]: false })); }
    };

    const testZebra = async (imp) => {
        if (!imp.printer_host) { toast.error("Manca l'IP della stampante"); return; }
        setTesting(t => ({ ...t, [imp.id]: true }));
        try {
            const { data } = await api.post(`/impostazioni/stampante-zebra/${imp.id}/test`);
            toast.success(data.message || "Etichetta di test inviata");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setTesting(t => ({ ...t, [imp.id]: false })); }
    };

    const saveA4 = async () => {
        setA4Busy(true);
        try {
            await api.put("/impostazioni/stampante-a4", { host: a4.host || null, port: parseInt(a4.port) || 9100 });
            setA4Dirty(false); toast.success("Stampante A4 salvata");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setA4Busy(false); }
    };

    const testA4 = () => {
        const win = window.open("", "_blank", "width=800,height=1000");
        if (!win) { toast.error("Popup bloccato: consenti popup per stampare"); return; }
        const ora = new Date().toLocaleString("it-IT");
        const doc = win.document;
        // Costruzione via DOM: evita document.write() e non introduce XSS
        doc.title = "Test stampa A4";
        const style = doc.createElement("style");
        style.textContent = `
            @page { size: A4; margin: 20mm; }
            body { font-family: system-ui, sans-serif; padding: 40px; }
            h1 { font-size: 42pt; margin: 0 0 20mm; letter-spacing: -1px; }
            .box { border: 3px solid #000; padding: 24pt; text-align: center; }
            .ora { font-family: monospace; font-size: 18pt; margin-top: 10mm; }
            .foot { position: fixed; bottom: 10mm; left: 20mm; right: 20mm; text-align: center; font-size: 10pt; color: #666; }
        `;
        doc.head.appendChild(style);
        const mk = (tag, cls, text) => {
            const el = doc.createElement(tag);
            if (cls) el.className = cls;
            if (text != null) el.textContent = text;
            return el;
        };
        const h1 = mk("h1", null, "TEST STAMPA");
        const box = mk("div", "box");
        box.appendChild(mk("div", null, "Se leggi questo foglio, la stampante A4 di default del dispositivo funziona."));
        box.appendChild(mk("div", "ora", ora));
        const foot = mk("div", "foot", "PRODUX · configura la stampante di default del sistema operativo per l'ufficio");
        doc.body.appendChild(h1);
        doc.body.appendChild(box);
        doc.body.appendChild(foot);
        setTimeout(() => { try { win.print(); } catch (e) { console.error(e); } }, 300);
    };

    return (
        <div className="grid gap-6 lg:grid-cols-2">
            <Card className="card p-5 md:p-6">
                <div className="flex items-center gap-3 mb-5">
                    <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                        <Printer size={18} />
                    </div>
                    <div>
                        <h2 className="font-display font-extrabold text-lg">Zebra — Etichette 76×51</h2>
                        <p className="text-xs text-slate-500">Una stampante per macchina, invio diretto TCP:9100</p>
                    </div>
                </div>
                {loading && <div className="text-slate-500 text-xs">Caricamento…</div>}
                <div className="space-y-4">
                    {impianti.map(imp => (
                        <div key={imp.id} className="border border-slate-800 rounded-lg p-4 space-y-3" data-testid={`imp-row-${imp.codice}`}>
                            <div className="flex items-center justify-between">
                                <div className="inline-flex items-center gap-2">
                                    <Factory size={14} className="text-amber-400" />
                                    <span className="font-mono text-amber-300">{imp.codice}</span>
                                    <span className="text-slate-400 text-sm">· {imp.descrizione}</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <div className="col-span-2">
                                    <Label className="text-[10px] text-slate-500 uppercase tracking-widest">IP / Host</Label>
                                    <Input data-testid={`imp-host-${imp.codice}`} value={imp.printer_host || ""}
                                           onChange={e=>updateImp(imp.id, {printer_host: e.target.value})}
                                           placeholder="192.168.1.50"
                                           className="bg-slate-950/50 border-slate-800 mt-1 font-mono h-9" />
                                </div>
                                <div>
                                    <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Porta</Label>
                                    <Input data-testid={`imp-port-${imp.codice}`} type="number" value={imp.printer_port || 9100}
                                           onChange={e=>updateImp(imp.id, {printer_port: e.target.value})}
                                           className="bg-slate-950/50 border-slate-800 mt-1 font-mono h-9" />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button size="sm" onClick={()=>saveImp(imp)} disabled={!imp._dirty || saving[imp.id]}
                                        data-testid={`imp-save-${imp.codice}`}
                                        className={`gap-1 ${imp._dirty ? "bg-amber-500 hover:bg-amber-400 text-slate-950" : "bg-slate-800 text-slate-500"}`}>
                                    <Save size={12} /> Salva
                                </Button>
                                <Button size="sm" variant="outline" onClick={()=>testZebra(imp)}
                                        disabled={!imp.printer_host || testing[imp.id]}
                                        data-testid={`imp-test-${imp.codice}`}
                                        className="bg-transparent border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 gap-1">
                                    <Send size={12} /> {testing[imp.id] ? "Invio…" : "Prova stampa"}
                                </Button>
                            </div>
                        </div>
                    ))}
                    {!loading && impianti.length === 0 && <div className="text-slate-500 text-xs">Nessuna macchina — aggiungi da Anagrafiche → Impianti</div>}
                </div>
            </Card>

            <Card className="card p-5 md:p-6 h-fit">
                <div className="flex items-center gap-3 mb-5">
                    <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-300">
                        <FileText size={18} />
                    </div>
                    <div>
                        <h2 className="font-display font-extrabold text-lg">Stampante A4 — fogli ordine</h2>
                        <p className="text-xs text-slate-500">Una per azienda · la stampa vera passa dal browser</p>
                    </div>
                </div>
                <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-2">
                            <Label className="text-[10px] text-slate-500 uppercase tracking-widest">IP / Host (promemoria)</Label>
                            <Input data-testid="a4-host" value={a4.host || ""}
                                   onChange={e=>{setA4({...a4, host: e.target.value}); setA4Dirty(true);}}
                                   placeholder="192.168.1.100"
                                   className="bg-slate-950/50 border-slate-800 mt-1 font-mono h-9" />
                        </div>
                        <div>
                            <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Porta</Label>
                            <Input data-testid="a4-port" type="number" value={a4.port || 9100}
                                   onChange={e=>{setA4({...a4, port: e.target.value}); setA4Dirty(true);}}
                                   className="bg-slate-950/50 border-slate-800 mt-1 font-mono h-9" />
                        </div>
                    </div>
                    <div className="text-[11px] text-slate-500 leading-relaxed border-l-2 border-blue-500/50 pl-3">
                        <strong className="text-slate-400">Nota:</strong> l'IP salvato qui è un promemoria per l'utente (per sapere quale stampante è "quella dell'ufficio"). La stampa vera dei fogli A4 passa sempre dal browser, che manda alla stampante di default del sistema operativo.
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={saveA4} disabled={!a4Dirty || a4Busy} data-testid="a4-save"
                                className={`gap-1 ${a4Dirty ? "bg-amber-500 hover:bg-amber-400 text-slate-950" : "bg-slate-800 text-slate-500"}`}>
                            <Save size={12} /> Salva
                        </Button>
                        <Button size="sm" variant="outline" onClick={testA4} data-testid="a4-test"
                                className="bg-transparent border-blue-500/40 text-blue-300 hover:bg-blue-500/10 gap-1">
                            <Send size={12} /> Prova stampa
                        </Button>
                    </div>
                </div>
            </Card>
            <StampanteBobineCard />
        </div>
    );
}

const TAB_ALIAS = {
    "/anagrafiche": "anagrafiche",
    "/ubicazioni": "ubicazioni",
    "/permessi": "permessi",
    "/impostazioni": null, // usa ?tab= o default
};

export default function Impostazioni() {
    const { user, can } = useAuth();
    const isAdmin = user?.role === "admin";
    const loc = useLocation();
    const nav = useNavigate();

    const canStampanti = isAdmin || can("impostazioni.stampanti");
    const canAnagr = isAdmin || can("anagrafiche.view");
    const canUbic = isAdmin || can("ubicazioni.view");
    const canMaccEstr = isAdmin || can("macchine_estr.edit");

    // Determina la tab iniziale: alias route > ?tab= > prima disponibile
    const aliasTab = TAB_ALIAS[loc.pathname];
    const qsTab = new URLSearchParams(loc.search).get("tab");
    const firstAvail = canStampanti ? "stampanti" : (canAnagr ? "anagrafiche" : (canUbic ? "ubicazioni" : (isAdmin ? "permessi" : "stampanti")));
    const initial = aliasTab || qsTab || firstAvail;
    const [tab, setTab] = useState(initial);

    // Sync URL quando cambio tab
    const onTabChange = (t) => {
        setTab(t);
        nav(`/impostazioni?tab=${t}`, { replace: true });
    };

    return (
        <div className="space-y-6">
            <div>
                <div className="text-[10px] tracking-[0.3em] font-mono text-amber-400/80 flex items-center gap-2">
                    <Cog size={12} /> IMPOSTAZIONI
                </div>
                <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Impostazioni</h1>
                <p className="mt-2 text-slate-400 text-sm max-w-2xl">Stampanti, anagrafiche di base, ubicazioni e permessi — tutto in un unico posto.</p>
            </div>

            <Tabs value={tab} onValueChange={onTabChange}>
                <TabsList data-testid="tabs-impostazioni" className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
                    {canStampanti && <TabsTrigger value="stampanti" data-testid="tab-imp-stampanti"><Printer size={14} className="mr-1"/> Stampanti</TabsTrigger>}
                    {canAnagr && <TabsTrigger value="anagrafiche" data-testid="tab-imp-anagrafiche"><Ribbon size={14} className="mr-1"/> Anagrafiche base</TabsTrigger>}
                    {canMaccEstr && <TabsTrigger value="macchine-estr" data-testid="tab-imp-macc-estr"><Wind size={14} className="mr-1"/> Macchinari estrusione</TabsTrigger>}
                    {isAdmin && <TabsTrigger value="permessi" data-testid="tab-imp-permessi"><ShieldCheck size={14} className="mr-1"/> Permessi</TabsTrigger>}
                </TabsList>
                {canStampanti && <TabsContent value="stampanti" className="mt-4"><StampantiTab /></TabsContent>}
                {canAnagr && <TabsContent value="anagrafiche" className="mt-4"><Anagrafiche embedded /></TabsContent>}
                {canMaccEstr && <TabsContent value="macchine-estr" className="mt-4"><EstrusioneMacchineTab /></TabsContent>}
                {isAdmin && <TabsContent value="permessi" className="mt-4"><Permessi embedded /></TabsContent>}
            </Tabs>
        </div>
    );
}
